import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Textarea } from 'app/components/ui/textarea';
import { useParams } from 'react-router-dom';
import { useProductsSlice } from 'app/pages/Product/slice';
import { toast } from 'sonner';
import { Tag, Save, X } from 'lucide-react';

interface AttributeFormInputs {
  name: string;
  slug: string;
  description?: string;
  id?: string;
}

const AddEditForm: React.FC<{
  selectedAttribute: AttributeFormInputs | null;
  onFormSubmit: (data: AttributeFormInputs) => void;
}> = ({ selectedAttribute, onFormSubmit }) => {
  const { id } = useParams();
  const {
    useAddProductsAttributeValuesMutation,
    useUpdateProductsAttributeValueMutation,
  } = useProductsSlice();
  const [addAttributeValue, { isLoading: isAdding }] =
    useAddProductsAttributeValuesMutation();
  const [updateAttributeValue, { isLoading: isUpdating }] =
    useUpdateProductsAttributeValueMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<AttributeFormInputs>();

  const nameValue = watch('name');

  // Helper function to generate slug from name
  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Auto-generate slug when name changes (but not when editing existing attribute)
  useEffect(() => {
    if (nameValue && !selectedAttribute) {
      const generatedSlug = generateSlug(nameValue);
      setValue('slug', generatedSlug);
    }
  }, [nameValue, setValue, selectedAttribute]);

  useEffect(() => {
    if (selectedAttribute) {
      reset(selectedAttribute);
    } else {
      reset({ name: '', slug: '', description: '' });
    }
  }, [selectedAttribute, reset]);

  const onSubmit: SubmitHandler<AttributeFormInputs> = async formData => {
    if (selectedAttribute) {
      // Updating existing attribute value
      try {
        await updateAttributeValue({
          id: selectedAttribute.id,
          ...formData,
        }).unwrap();

        toast.success('Attribute value updated successfully!');
        onFormSubmit(formData);
      } catch (error) {
        toast.error('Failed to update attribute value. Please try again.');
      }
    } else {
      // Adding new attribute value
      try {
        const requestData = {
          attribute_id: id,
          ...formData,
        };
        await addAttributeValue({ data: requestData, id }).unwrap();
        toast.success('Attribute value added successfully!');
        onFormSubmit(formData);
      } catch (error) {
        toast.error('Failed to add attribute value. Please try again.');
      }
    }

    reset();
  };

  const handleCancel = () => {
    reset();
    onFormSubmit({ name: '', slug: '', description: '' });
  };

  return (
    <div className="p-6">
      {/* Form Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
          <Tag className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {selectedAttribute ? 'Edit Attribute Value' : 'Add New Value'}
          </h2>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {selectedAttribute
              ? 'Update value information'
              : 'Create a new attribute value'}
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
              Important Note
            </h4>
            <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
              Attribute values can be assigned to products and variations.
              Deleting a value will remove it from all products and variations
              to which it has been assigned. Recreating a value will not
              automatically assign it back to products.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Name Field */}
        <div className="space-y-2">
          <Label
            htmlFor="name"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mr-2"></span>
            Value Name <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input
            type="text"
            id="name"
            placeholder="Enter value name (e.g., Red, Large, Premium)"
            {...register('name', { required: 'Name is required' })}
            className={`h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 rounded-lg transition-all duration-200 text-sm ${errors.name
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : ''
              }`}
          />
          {errors.name && (
            <p className="text-red-500 text-xs font-medium">
              {errors.name.message}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            Display name shown on the front-end
          </p>
        </div>

        {/* Slug Field */}
        <div className="space-y-2">
          <Label
            htmlFor="slug"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full mr-2"></span>
            Value Slug <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input
            type="text"
            id="slug"
            placeholder="Auto-generated from name"
            {...register('slug', {
              required: 'Slug is required',
              pattern: {
                value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                message: 'Slug must be alphanumeric and kebab-case',
              },
            })}
            disabled={!selectedAttribute}
            className={`h-10 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 rounded-lg transition-all duration-200 text-sm ${errors.slug
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : ''
              } ${!selectedAttribute
                ? 'cursor-not-allowed opacity-60'
                : 'bg-white dark:bg-gray-800'
              }`}
          />
          {errors.slug && (
            <p className="text-red-500 text-xs font-medium">
              {errors.slug.message}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            {!selectedAttribute
              ? 'Auto-generated from value name'
              : 'Unique identifier for the value (lowercase, kebab-case)'}
          </p>
        </div>

        {/* Description Field */}
        <div className="space-y-2">
          <Label
            htmlFor="description"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-rose-500 to-red-500 rounded-full mr-2"></span>
            Description{' '}
            <span className="text-gray-500 dark:text-gray-400 text-xs ml-1">
              (Optional)
            </span>
          </Label>
          <Textarea
            id="description"
            placeholder="Enter value description (optional)"
            rows={3}
            {...register('description')}
            className={`bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 rounded-lg transition-all duration-200 text-sm resize-none ${errors.description
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : ''
              }`}
          />
          {errors.description && (
            <p className="text-red-500 text-xs font-medium">
              {errors.description.message}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            Detailed description of the attribute value (optional)
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-700">
          {selectedAttribute && (
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="h-10 px-4 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 transition-all duration-200"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            disabled={isAdding || isUpdating}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white transition-all duration-200 disabled:opacity-50 h-10 px-6"
          >
            <Save className="w-4 h-4 mr-2" />
            {isAdding || isUpdating
              ? 'Saving...'
              : selectedAttribute
                ? 'Save Changes'
                : 'Add Value'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddEditForm;
